import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { Settings2 } from 'lucide-react';
import type { FallbackChainEntry, FlowType } from '@/types/service';
import {
  IQ_SERVICE_KEY_PATTERN,
  type IqServiceInventoryStatus,
} from '@/types/iq-service-inventory';

export type RoutingEntryDialogMode = 'add' | 'edit';

/**
 * Gateway-service option for the UC-5 dropdown, sourced from the merchant's
 * IQ-service inventory. `flowType` is the (optional) operator annotation used to
 * client-side filter by the selected slot flow; unset = visible in every slot.
 *
 * Spec 347 §7: `status` is an optional annotation only — the non-active status is
 * appended to the option label (in parentheses). No link, no counter, no navigation.
 */
export interface IqGatewayServiceOption {
  value: string;
  label: string;
  flowType?: FlowType | null;
  status?: IqServiceInventoryStatus;
}

interface RoutingEntryDialogProps {
  open: boolean;
  mode: RoutingEntryDialogMode;
  /** Bei `mode='edit'` vorhandene Werte zum Vorbefuellen. */
  initial?: FallbackChainEntry;
  /**
   * GatewayServiceIDs, die bereits in der Chain stecken — fuer EC-6
   * (Duplikat-Pruefung). Im Edit-Modus muss der eigene Wert ausgeschlossen
   * sein, sonst meldet der Validator faelschlich "Duplikat".
   */
  existingGatewayIds: string[];
  /** Credential-Set-Combobox-Optionen aus `useIqServices` (iqServiceId-Feld). */
  iqServiceOptions: ComboboxOption[];
  /** Echte Liste laedt noch — Combobox bleibt im Loading-State. */
  iqServiceOptionsLoading?: boolean;
  /**
   * UC-5: gatewayServiceId-Dropdown aus dem Merchant-IQ-Service-Inventar (nur
   * `active`). Wird clientseitig auf den gewaehlten `flowType` gefiltert; freie
   * Eingabe bleibt als Override moeglich.
   */
  gatewayServiceOptions?: IqGatewayServiceOption[];
  gatewayServiceOptionsLoading?: boolean;
  onSubmit: (entry: FallbackChainEntry) => void;
  onCancel: () => void;
}

type Errors = Partial<Record<'flowType' | 'gatewayServiceId' | 'iqServiceId', string>>;

// Spec §4.3.1 (v4): Client-Validation ist strenger als Contract-Pattern.
const IQ_SERVICE_ID_REGEX = /^iq_[a-z0-9][a-z0-9_]*$/;

export function validateEntry(
  entry: FallbackChainEntry,
  existingGatewayIds: string[],
  t: (key: string) => string,
): Errors {
  const errors: Errors = {};

  if (!entry.flowType) {
    errors.flowType = t('dialog.error.flowTypeRequired');
  }

  if (!entry.gatewayServiceId) {
    errors.gatewayServiceId = t('dialog.error.gatewayIdRequired');
  } else if (!IQ_SERVICE_KEY_PATTERN.test(entry.gatewayServiceId)) {
    // Spec 344 UC-5: relaxed to the opaque inventory-key rule (only structural
    // breakers excluded). The former strict pattern + `^iq_` rejection wrongly
    // rejected valid inventory keys (digit-start, up to 128 chars).
    errors.gatewayServiceId = t('dialog.error.gatewayIdPattern');
  } else if (existingGatewayIds.includes(entry.gatewayServiceId)) {
    errors.gatewayServiceId = t('dialog.error.gatewayIdDuplicate');
  }

  if (entry.iqServiceId) {
    if (entry.iqServiceId.length > 100) {
      errors.iqServiceId = t('dialog.error.iqServiceIdTooLong');
    } else if (!IQ_SERVICE_ID_REGEX.test(entry.iqServiceId)) {
      errors.iqServiceId = t('dialog.error.iqServiceIdPattern');
    }
  }

  return errors;
}

/**
 * Modal-Dialog zum Anlegen/Bearbeiten eines `FallbackChainEntry`
 * (Spec 0216 §5.3 + §6.6 EC-3/EC-6).
 *
 *  - **gatewayServiceId** ist die InternetQ-Service-Kennung (`wap_…`/`web_…`)
 *    und wird als Freitext erfasst — `IqServiceDetail` liefert keine
 *    Gateway-IDs (Issue #230). Eine echte Datenquelle waere ein Folge-Request
 *    ans Facade-Team.
 *  - **iqServiceId** ist das Credential-Set (`iq_…`) und nutzt die
 *    `iqServiceOptions`-Combobox (separates Feld, nie im Gateway-Feld).
 *  - Strikte Client-Validation gem. Spec §4.3.1 (IqServiceID Pattern).
 *  - Duplikat-Pruefung gegen `existingGatewayIds` (EC-6).
 */
export function RoutingEntryDialog({
  open,
  mode,
  initial,
  existingGatewayIds,
  iqServiceOptions,
  iqServiceOptionsLoading = false,
  gatewayServiceOptions = [],
  gatewayServiceOptionsLoading = false,
  onSubmit,
  onCancel,
}: RoutingEntryDialogProps) {
  const { t } = useTranslation('routing');
  const { t: tInv } = useTranslation('iq-service-inventory');
  const [flowType, setFlowType] = useState<FlowType | ''>('');
  const [gatewayServiceId, setGatewayServiceId] = useState('');
  const [iqServiceId, setIqServiceId] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  // UC-5: filter the inventory dropdown to the selected slot flowType. An option
  // without a flowType annotation is visible everywhere; before a flowType is
  // chosen, all options show. Free-text override stays available (allowFreeText).
  // Spec 347 §7: append the status to the label for non-active keys (annotation only).
  const gatewayOptions: ComboboxOption[] = gatewayServiceOptions
    .filter((o) => !o.flowType || flowType === '' || o.flowType === flowType)
    .map((o) => ({
      value: o.value,
      label:
        o.status && o.status !== 'active'
          ? `${o.label} (${tInv(`status.${o.status}`)})`
          : o.label,
    }));

  // Reset bei jedem Open: vorbefuellen im Edit-Modus, sonst leer.
  useEffect(() => {
    if (!open) return;
    setFlowType(initial?.flowType ?? '');
    setGatewayServiceId(initial?.gatewayServiceId ?? '');
    setIqServiceId(initial?.iqServiceId ?? '');
    setShowAdvanced(!!initial?.iqServiceId);
    setErrors({});
  }, [open, initial]);

  const handleSubmit = () => {
    const draft: FallbackChainEntry = {
      flowType: flowType as FlowType,
      gatewayServiceId,
      ...(iqServiceId ? { iqServiceId } : {}),
    };
    const next = validateEntry(draft, existingGatewayIds, t);
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit(draft);
  };

  const titleKey = mode === 'add' ? 'dialog.title.add' : 'dialog.title.edit';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t(titleKey)}</DialogTitle>
          <DialogDescription>{t('dialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="routing-entry-flow-type">
              {t('dialog.label.flowType')} <span className="text-destructive">*</span>
            </Label>
            <Select
              value={flowType}
              onValueChange={(v) => setFlowType(v as FlowType)}
            >
              <SelectTrigger id="routing-entry-flow-type" aria-invalid={!!errors.flowType}>
                <SelectValue placeholder={t('dialog.placeholder.flowType')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="WAP_CLICK">WAP_CLICK</SelectItem>
                <SelectItem value="WEB_TAN">WEB_TAN</SelectItem>
              </SelectContent>
            </Select>
            {errors.flowType && (
              <p className="text-xs text-destructive">{errors.flowType}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="routing-entry-gateway-id">
              {t('dialog.label.gatewayServiceId')} <span className="text-destructive">*</span>
            </Label>
            <Combobox
              id="routing-entry-gateway-id"
              options={gatewayOptions}
              value={gatewayServiceId}
              onChange={setGatewayServiceId}
              placeholder={t('dialog.placeholder.gatewayServiceId')}
              searchPlaceholder={t('dialog.placeholder.gatewayServiceId')}
              emptyText={t('dialog.empty.gatewayFreeText')}
              allowFreeText
              loading={gatewayServiceOptionsLoading}
              error={!!errors.gatewayServiceId}
            />
            {errors.gatewayServiceId ? (
              <p className="text-xs text-destructive">{errors.gatewayServiceId}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t('dialog.hint.gatewayIdPattern')}
              </p>
            )}
          </div>

          <div>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              onClick={() => setShowAdvanced((v) => !v)}
            >
              <Settings2 className="h-3.5 w-3.5" />
              {t('dialog.label.advancedToggle')}
            </button>
          </div>

          {showAdvanced && (
            <div className="space-y-1.5 rounded-md border bg-muted/20 p-3">
              <Label
                htmlFor="routing-entry-iq-id"
                title={t('dialog.tooltip.iqServiceIdOverride')}
              >
                {t('dialog.label.iqServiceIdOverride')}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t('dialog.tooltip.iqServiceIdOverride')}
              </p>
              <Combobox
                id="routing-entry-iq-id"
                options={iqServiceOptions}
                value={iqServiceId}
                onChange={setIqServiceId}
                placeholder={t('dialog.placeholder.iqServiceId')}
                searchPlaceholder={t('dialog.placeholder.iqServiceId')}
                emptyText={t('dialog.empty.iqServices')}
                allowFreeText
                loading={iqServiceOptionsLoading}
                error={!!errors.iqServiceId}
              />
              {errors.iqServiceId ? (
                <p className="text-xs text-destructive">{errors.iqServiceId}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t('dialog.hint.iqServiceIdPattern')}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t('dialog.button.cancel')}
          </Button>
          <Button onClick={handleSubmit}>
            {t('dialog.button.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
